// Textures for the table and the room, at the resolution of the graphics quality (quality.ts textureScale).
// Images are fetched once and kept, so a quality switch rebuilds its textures at once from memory (no flash of
// untextured surfaces); canvases drawn at start-up (roomTextures.ts) are drawn once and scaled the same way.
declare const THREE: any;

const images = new Map<string, HTMLImageElement>();
const canvases = new Map<string, HTMLCanvasElement>();
/** live THREE.Texture objects by key@scale, so one build shares them; dropped by releaseOtherScales */
const textures = new Map<string, {scale: number, texture: any}>();

function scaled(src: HTMLImageElement | HTMLCanvasElement, scale: number): HTMLImageElement | HTMLCanvasElement {
    if (scale >= 1) return src;
    const w = Math.max(1, Math.round(((src as any).naturalWidth || src.width) * scale));
    const h = Math.max(1, Math.round(((src as any).naturalHeight || src.height) * scale));
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    const g = c.getContext("2d");
    if (!g) return src;
    g.imageSmoothingEnabled = true;
    g.drawImage(src, 0, 0, w, h);
    return c;
}

function newTexture(repeat: boolean, anisotropy: number) {
    const t = new THREE.Texture();
    if (repeat) t.wrapS = t.wrapT = THREE.RepeatWrapping;
    if (anisotropy > 1) t.anisotropy = anisotropy;
    return t;
}

/** A texture from an image file (public/textures/...), repeating, at `scale` of its resolution. */
export function imageTexture(path: string, scale: number, anisotropy = 1) {
    const key = `${path}@${scale}@${anisotropy}`;
    const have = textures.get(key);
    if (have) return have.texture;
    const t = newTexture(true, anisotropy);
    const apply = (img: HTMLImageElement) => { t.image = scaled(img, scale); t.needsUpdate = true; };
    let img = images.get(path);
    if (img && img.complete && img.naturalWidth > 0) {
        apply(img);
    } else {
        if (!img) {
            img = new Image();
            images.set(path, img);
            img.src = path;
        }
        const i = img;
        i.addEventListener("load", () => apply(i));
    }
    textures.set(key, {scale, texture: t});
    return t;
}

/** A texture from a canvas drawn once by `draw` (cached by name), at `scale` of its resolution. */
export function canvasTexture(name: string, draw: () => HTMLCanvasElement, scale: number, repeat = true) {
    const key = `canvas:${name}@${scale}`;
    const have = textures.get(key);
    if (have) return have.texture;
    let c = canvases.get(name);
    if (!c) { c = draw(); canvases.set(name, c); }
    const t = newTexture(repeat, 1);
    t.image = scaled(c, scale);
    t.needsUpdate = true;
    textures.set(key, {scale, texture: t});
    return t;
}

/** After a quality switch: free the GPU copies made for the other resolution. */
export function releaseOtherScales(scale: number) {
    textures.forEach((v, key) => {
        if (v.scale !== scale) {
            if (v.texture.dispose) v.texture.dispose();
            textures.delete(key);
        }
    });
}
